/**
 * Authentication Types and Interfaces
 * Defines all authentication-related types for the Billing Portal
 */

/**
 * JWT Authentication Token
 */
export interface AuthToken {
  token: string;
  refreshToken: string;
  expiresAt: number; // Unix timestamp in milliseconds
}

/**
 * User roles in the system
 */
export type UserRole = 'super_admin' | 'admin' | 'partner';

/**
 * User information
 */
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  communityId: string;
  communityName: string;
}

/**
 * Authentication state managed by Zustand store
 */
export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  expiresAt?: number | null; // Token expiration timestamp in milliseconds
}

/**
 * Authentication API request types
 */
export interface ValidateTokenRequest {
  token: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

/**
 * Authentication API response types
 */
export interface ValidateTokenResponse {
  valid: boolean;
  user?: User;
  expiresAt?: number;
}

export interface RefreshTokenResponse {
  token: string;
  refreshToken: string;
  expiresAt: number;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  expiresAt: number;
  user: User;
}

/**
 * Error codes for authentication operations
 */
export const AuthErrorCode = {
  TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
  TOKEN_INVALID: 'AUTH_TOKEN_INVALID',
  UNAUTHORIZED: 'AUTH_UNAUTHORIZED',
  REFRESH_FAILED: 'AUTH_REFRESH_FAILED',
  VALIDATION_FAILED: 'AUTH_VALIDATION_FAILED',
} as const;

export type AuthErrorCode = typeof AuthErrorCode[keyof typeof AuthErrorCode];

/**
 * API Error types
 */
export const ErrorCode = {
  // Authentication errors
  AUTH_TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
  AUTH_TOKEN_INVALID: 'AUTH_TOKEN_INVALID',
  AUTH_UNAUTHORIZED: 'AUTH_UNAUTHORIZED',
  AUTH_REFRESH_FAILED: 'AUTH_REFRESH_FAILED',
  
  // Payment errors
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  PAYMENT_METHOD_INVALID: 'PAYMENT_METHOD_INVALID',
  
  // Subscription errors
  SUBSCRIPTION_NOT_FOUND: 'SUBSCRIPTION_NOT_FOUND',
  
  // Promo code errors
  PROMO_CODE_INVALID: 'PROMO_CODE_INVALID',
  PROMO_CODE_EXPIRED: 'PROMO_CODE_EXPIRED',
  
  // Generic errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ErrorCode = typeof ErrorCode[keyof typeof ErrorCode];

/**
 * API Error interface
 */
export interface ApiError {
  code: ErrorCode;
  message: string;
  details?: Record<string, any>;
  statusCode: number;
}

/**
 * Type guard to check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'statusCode' in error
  );
}
